// =============================================================================
//  MonOrganisationPage (route /tenants pour compat, mais l UI est single tenant)
//
//  Affiche les infos du SEUL tenant Microsoft 365 audite par ce compte NIS365 :
//  identite, plan, statut consent, dernier scan. Plus de switcher, plus de
//  liste multi-clients.
// =============================================================================

import {
  Card,
  CardHeader,
  Title3,
  Body1,
  Caption1,
  Button,
  tokens,
  Badge,
  Divider,
  MessageBar,
  MessageBarBody,
  Spinner,
} from '@fluentui/react-components';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { AzureConnectionWizard } from '../components/AzureConnectionWizard';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building24Regular,
  ShieldCheckmark20Regular,
  Calendar20Regular,
  PeopleTeam20Regular,
  PlugConnected20Regular,
  ArrowSync20Regular,
  Edit20Regular,
  Person20Regular,
  Save20Regular,
  ArrowDownload20Regular,
} from '@fluentui/react-icons';
import { Input, Field as F2Field } from '@fluentui/react-components';
import { PageHeader } from '../components/PageHeader';
import { BrandIcon } from '../components/BrandIcon';
import { ScanScopeSection } from './SettingsPage';
import { useActiveTenant } from '../hooks/useActiveTenant';
import { useLatestScan } from '../hooks/useScans';
import { planTierByPlan, resolvePlanFromSize } from '../domain/mockTenants';
import { statusColors } from '../theme/brandTheme';
import { onboardingApi, type OnboardingStatus, type TestPermissionsResponse, type PermissionTestResult } from '../api/onboardingApi';
import { azureConnectionsApi, type AzureConnection } from '../api/azureConnectionsApi';
import { useLicense } from '../auth/LicenseGate';
import { Link } from 'react-router-dom';
import { Sparkle24Filled, LockClosed24Regular } from '@fluentui/react-icons';

export function TenantsPage() {
  const { t, i18n }  = useTranslation();
  const navigate     = useNavigate();
  const { tenant }   = useActiveTenant();
  const latestScan   = useLatestScan(tenant.id);
  const currentTier  = planTierByPlan[tenant.subscriptionPlan];
  const recommended  = resolvePlanFromSize(tenant.size ?? 0);
  // v2026.06.86 : ne PAS afficher l alerte mismatch si size pas renseignee
  // (sinon faux positif + plante a la ligne 584 qui formate tenant.size).
  const planMismatch = (tenant.size ?? 0) > 0 && recommended.plan !== tenant.subscriptionPlan;
  const license      = useLicense();

  const hasActiveLicense = Boolean(license.hasLicense && license.status?.valid);

  type OrgTab = 'org' | 'm365' | 'azure' | 'advanced';
  const [orgTab, setOrgTab] = useState<OrgTab>('org');

  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatus | null>(null);
  const [testingConn, setTestingConn]           = useState(false);
  const [testResult, setTestResult]             = useState<{ ok: boolean; msg: string } | null>(null);
  // downloadingExoScript retire 2026.06 - migration Graph (Phase 3).
  const [permsDiag, setPermsDiag]               = useState<TestPermissionsResponse | null>(null);
  const [permsLoading, setPermsLoading]         = useState(false);
  const [permsError, setPermsError]             = useState<string | null>(null);
  const [permsOpen, setPermsOpen]               = useState(false);

  // Telechargement du script EXO/Purview (re-active 2026.07 : auto-eval EXO REST).
  const [scriptLoading, setScriptLoading] = useState(false);
  async function onDownloadExoScript() {
    setScriptLoading(true);
    setTestResult(null);
    try {
      await onboardingApi.downloadSetupScript();
    } catch (e) {
      setTestResult({ ok: false, msg: (e as Error)?.message ?? 'Téléchargement du script échoué' });
    } finally {
      setScriptLoading(false);
    }
  }

  async function onTestPermissions() {
    setPermsOpen(true);
    setPermsLoading(true);
    setPermsError(null);
    try {
      const r = await onboardingApi.testPermissions();
      setPermsDiag(r);
    } catch (e) {
      setPermsError((e as Error)?.message ?? 'Test failed');
    } finally {
      setPermsLoading(false);
    }
  }

  // Etat editable pour la customisation entreprise (locale pour MVP)
  const [companyName, setCompanyName] = useState(onboardingStatus?.organizationName ?? tenant.name ?? '');
  const [techContact, setTechContact] = useState(license.status?.activatedBy ?? '');
  const [savedSnapshot, setSavedSnapshot] = useState<{ name: string; contact: string } | null>(null);
  // Identité légale (page de garde + chapitre du rapport CyFun)
  const EMPTY_LEGAL = { companyLegalName: '', companyBce: '', companyAddress: '', companyPostalCode: '', companyCity: '', companyCountry: '' };
  const [legal, setLegal] = useState(EMPTY_LEGAL);
  const [legalSnapshot, setLegalSnapshot] = useState(EMPTY_LEGAL);
  // Logo de l'organisation (data URL) — utilisé dans les rapports/documents.
  const [logo, setLogo] = useState<string>('');
  const [logoSnapshot, setLogoSnapshot] = useState<string>('');
  const [logoError, setLogoError] = useState<string | null>(null);
  const isDirty = (companyName !== savedSnapshot?.name) || (techContact !== savedSnapshot?.contact)
    || JSON.stringify(legal) !== JSON.stringify(legalSnapshot) || (logo !== logoSnapshot);

  // Redimensionne l'image importée (max 320 px, PNG) → data URL léger.
  function onLogoFile(file: File) {
    setLogoError(null);
    if (!file.type.startsWith('image/')) { setLogoError('Fichier image requis (PNG, JPG, SVG…).'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const src = String(reader.result);
      // On rasterise TOUT en PNG (y compris SVG) : les documents Word n'embarquent
      // pas le SVG, donc le logo doit être un raster pour apparaître dans les rapports.
      const img = new Image();
      img.onload = () => {
        const max = 320;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) { setLogoError('Impossible de traiter l’image.'); return; }
        ctx.drawImage(img, 0, 0, w, h);
        setLogo(canvas.toDataURL('image/png'));
      };
      img.onerror = () => setLogoError('Image illisible.');
      img.src = src;
    };
    reader.readAsDataURL(file);
  }

  // Recharge la valeur initiale quand on a l onboardingStatus.
  // Priorite : champs perso persistes (companyName/techContact) > orgName MS / email license.
  useEffect(() => {
    if (!onboardingStatus || savedSnapshot) return;
    const initialName    = onboardingStatus.companyName ?? onboardingStatus.organizationName ?? '';
    const initialContact = onboardingStatus.techContact ?? license.status?.activatedBy ?? '';
    setCompanyName(initialName);
    setTechContact(initialContact);
    setSavedSnapshot({ name: initialName, contact: initialContact });
    const initialLegal = {
      companyLegalName:  onboardingStatus.companyLegalName  ?? '',
      companyBce:        onboardingStatus.companyBce        ?? '',
      companyAddress:    onboardingStatus.companyAddress    ?? '',
      companyPostalCode: onboardingStatus.companyPostalCode ?? '',
      companyCity:       onboardingStatus.companyCity       ?? '',
      companyCountry:    onboardingStatus.companyCountry    ?? '',
    };
    setLegal(initialLegal);
    setLegalSnapshot(initialLegal);
    const initialLogo = onboardingStatus.companyLogo ?? '';
    setLogo(initialLogo);
    setLogoSnapshot(initialLogo);
  }, [onboardingStatus]);

  const [savingCustomization, setSavingCustomization] = useState(false);

  async function saveOrgInfo() {
    setSavingCustomization(true);
    setTestResult(null);
    try {
      const r = await onboardingApi.updateCustomization({
        companyName: companyName.trim(),
        techContact: techContact.trim(),
        companyLegalName:  legal.companyLegalName.trim(),
        companyBce:        legal.companyBce.trim(),
        companyAddress:    legal.companyAddress.trim(),
        companyPostalCode: legal.companyPostalCode.trim(),
        companyCity:       legal.companyCity.trim(),
        companyCountry:    legal.companyCountry.trim(),
        companyLogo:       logo,
      });
      setLogoSnapshot(logo);
      setSavedSnapshot({ name: r.companyName, contact: r.techContact });
      setCompanyName(r.companyName);
      setTechContact(r.techContact);
      const newLegal = {
        companyLegalName:  r.companyLegalName  ?? '',
        companyBce:        r.companyBce        ?? '',
        companyAddress:    r.companyAddress    ?? '',
        companyPostalCode: r.companyPostalCode ?? '',
        companyCity:       r.companyCity       ?? '',
        companyCountry:    r.companyCountry    ?? '',
      };
      setLegal(newLegal);
      setLegalSnapshot(newLegal);
      setTestResult({ ok: true, msg: t('tenants.customization.saved') });
      // Refresh onboardingStatus pour propager companyName et techContact partout
      try {
        const s = await onboardingApi.status();
        setOnboardingStatus(s);
      } catch {}
    } catch (e) {
      setTestResult({ ok: false, msg: (e as Error).message });
    } finally {
      setSavingCustomization(false);
    }
  }

  // ----------------------------------------------------------------------------
  // Sante des permissions Graph : on regarde dans le dernier scan combien de
  // checks ont retourne status=error avec un message Forbidden/Unauthorized.
  // ----------------------------------------------------------------------------
  const permsHealth = (() => {
    const results = (latestScan as any)?.results as Array<{ Status?: string; status?: string; Message?: string; message?: string }> | undefined;
    if (!results || results.length === 0) return null;
    const errs = results.filter(r => {
      const st = (r.Status ?? r.status ?? '').toLowerCase();
      const msg = (r.Message ?? r.message ?? '').toLowerCase();
      return st === 'error' && (msg.includes('forbidden') || msg.includes('unauthorized') || msg.includes('403') || msg.includes('401'));
    });
    return {
      totalErrors: errs.length,
      totalChecks: results.length,
      ok: errs.length === 0,
    };
  })();

  useEffect(() => {
    onboardingApi.status().then(setOnboardingStatus).catch(() => {});
  }, []);

  async function runQuickTest() {
    setTestingConn(true);
    setTestResult(null);
    try {
      const r = await onboardingApi.testConnection();
      setTestResult({
        ok:  r.success,
        msg: r.success
          ? t('tenants.connection.okPing', { name: r.organizationName ?? 'tenant', tenantId: r.tenantId })
          : t('tenants.connection.failPing', { error: r.error ?? '—' })
              + (r.hint ? t('tenants.connection.failPingHint', { hint: r.hint }) : ''),
      });
      // Recharge le status apres test (met a jour lastTestAt/lastTestSuccess)
      const s = await onboardingApi.status();
      setOnboardingStatus(s);
    } catch (e) {
      setTestResult({ ok: false, msg: (e as Error).message });
    } finally {
      setTestingConn(false);
    }
  }

  return (
    <div>
      <PageHeader
        title={t('tenants.title')}
        description={t('tenants.subtitleFull')}
        actions={
          <RouterLink to="/billing" style={{ textDecoration: 'none' }}>
            <Button appearance="secondary">{t('tenants.viewBilling')}</Button>
          </RouterLink>
        }
      />

      {/* Upsell si pas de licence active */}
      {!hasActiveLicense && (
        <Card style={{
          background: 'linear-gradient(135deg, #FFF3D6 0%, #FFE8B5 100%)',
          border: '1.5px solid #D9842B',
          padding: 20,
          marginBottom: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <LockClosed24Regular style={{ color: '#B45309' }} />
            <div style={{ flex: 1 }}>
              <Title3 style={{ color: '#8A6308', margin: 0 }}>
                {t('tenants.upsell.title')}
              </Title3>
              <Caption1 style={{ color: '#8A6308', display: 'block', marginTop: 4 }}>
                {t('tenants.upsell.body')}
              </Caption1>
            </div>
            <Link
              to="/license"
              style={{
                background: '#1F3D5C', color: '#fff',
                padding: '10px 18px', borderRadius: 8,
                fontWeight: 700, fontSize: 14, textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {t('tenants.upsell.cta')}
            </Link>
          </div>
        </Card>
      )}

      {/* Onglets Organisation (style Compliance Manager) */}
      <div style={{
        display: 'inline-flex', gap: 6, padding: 4,
        background: tokens.colorNeutralBackground2,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: 8, marginBottom: 16, flexWrap: 'wrap',
      }}>
        {([
          { key: 'org',      label: t('tenants.tabs.org', 'Paramètres de l’organisation'), icon: <Building24Regular /> },
          { key: 'm365',     label: t('tenants.tabs.m365', 'Paramètres Microsoft 365'),    icon: <BrandIcon name="m365" size={16} /> },
          { key: 'azure',    label: t('tenants.tabs.azure', 'Paramètres Azure'),           icon: <BrandIcon name="azure" size={16} /> },
          { key: 'advanced', label: t('tenants.tabs.advanced', 'Avancés'),                 icon: <Edit20Regular /> },
        ] as Array<{ key: OrgTab; label: string; icon: React.ReactNode }>).map((tb) => {
          const active = orgTab === tb.key;
          return (
            <button
              key={tb.key}
              onClick={() => setOrgTab(tb.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px',
                border: 'none', borderRadius: 6, cursor: 'pointer',
                background: active ? tokens.colorBrandBackground : 'transparent',
                color: active ? tokens.colorNeutralForegroundOnBrand : tokens.colorNeutralForeground1,
                fontWeight: active ? 600 : 500, fontSize: 14,
              }}
            >
              <span style={{ display: 'inline-flex', width: 18, height: 18, alignItems: 'center', color: active ? tokens.colorNeutralForegroundOnBrand : tokens.colorNeutralForeground2 }}>{tb.icon}</span>
              {tb.label}
            </button>
          );
        })}
      </div>

      {/* ===== Personnalisation entreprise (license requise) ===== */}
      {orgTab === 'org' && hasActiveLicense && (
        <Card style={{ marginBottom: 24 }}>
          <CardHeader
            image={<Edit20Regular primaryFill={tokens.colorBrandForeground1}/>}
            header={<Title3>{t('tenants.customization.title')}</Title3>}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 12 }}>
            <F2Field label={t('tenants.customization.companyName')} hint={t('tenants.customization.companyHint')}>
              <Input
                value={companyName}
                onChange={(_, d) => setCompanyName(d.value)}
                placeholder={onboardingStatus?.organizationName ?? t('tenants.customization.companyPlaceholder')}
              />
            </F2Field>
            <F2Field label={t('tenants.customization.techContact')} hint={t('tenants.customization.techHint')}>
              <Input
                type="email"
                value={techContact}
                onChange={(_, d) => setTechContact(d.value)}
                placeholder={t('tenants.customization.techPlaceholder')}
                contentBefore={<Person20Regular />}
              />
            </F2Field>
          </div>

          {/* Logo de l'organisation — utilisé dans les rapports & documents AutoDoc */}
          <div style={{ marginTop: 20 }}>
            <Caption1 style={{ color: tokens.colorNeutralForeground3, textTransform: 'uppercase', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, display: 'block', marginBottom: 8 }}>
              Logo de l’organisation (rapports)
            </Caption1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div style={{
                width: 120, height: 72, borderRadius: 8, border: `1px dashed ${tokens.colorNeutralStroke2}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', background: tokens.colorNeutralBackground2, overflow: 'hidden',
              }}>
                {logo
                  ? <img src={logo} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  : <Caption1 style={{ color: tokens.colorNeutralForeground4 }}>Aperçu</Caption1>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button appearance="secondary" onClick={() => document.getElementById('org-logo-input')?.click()}>
                    {logo ? 'Changer le logo' : 'Importer un logo'}
                  </Button>
                  {logo && <Button appearance="subtle" onClick={() => setLogo('')}>Retirer</Button>}
                </div>
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>PNG, JPG ou SVG — redimensionné automatiquement (max 320 px).</Caption1>
                {logoError && <Caption1 style={{ color: tokens.colorPaletteRedForeground1 }}>{logoError}</Caption1>}
              </div>
              <input
                id="org-logo-input"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onLogoFile(f); e.currentTarget.value = ''; }}
              />
            </div>
          </div>

          {/* Identité légale — alimente la page de garde + le chapitre entreprise du rapport CyFun */}
          <div style={{ marginTop: 20 }}>
            <Caption1 style={{ color: tokens.colorNeutralForeground3, textTransform: 'uppercase', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, display: 'block', marginBottom: 8 }}>
              {t('tenants.legal.section', 'Identité légale (rapport CyFun)')}
            </Caption1>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <F2Field label={t('tenants.legal.legalName', 'Dénomination légale')}>
                <Input value={legal.companyLegalName} onChange={(_, d) => setLegal((p) => ({ ...p, companyLegalName: d.value }))} placeholder="Ex. HYBEOS SRL" />
              </F2Field>
              <F2Field label={t('tenants.legal.bce', "N° d'entreprise (BCE)")}>
                <Input value={legal.companyBce} onChange={(_, d) => setLegal((p) => ({ ...p, companyBce: d.value }))} placeholder="BE 0123.456.789" />
              </F2Field>
              <F2Field label={t('tenants.legal.address', 'Adresse')} style={{ gridColumn: '1 / -1' }}>
                <Input value={legal.companyAddress} onChange={(_, d) => setLegal((p) => ({ ...p, companyAddress: d.value }))} placeholder="Rue et numéro" />
              </F2Field>
              <F2Field label={t('tenants.legal.postalCode', 'Code postal')}>
                <Input value={legal.companyPostalCode} onChange={(_, d) => setLegal((p) => ({ ...p, companyPostalCode: d.value }))} />
              </F2Field>
              <F2Field label={t('tenants.legal.city', 'Ville')}>
                <Input value={legal.companyCity} onChange={(_, d) => setLegal((p) => ({ ...p, companyCity: d.value }))} />
              </F2Field>
              <F2Field label={t('tenants.legal.country', 'Pays')}>
                <Input value={legal.companyCountry} onChange={(_, d) => setLegal((p) => ({ ...p, companyCountry: d.value }))} placeholder="Belgique" />
              </F2Field>
            </div>
          </div>

          <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button
              appearance="primary"
              icon={savingCustomization ? <Spinner size="tiny" /> : <Save20Regular />}
              disabled={!isDirty || savingCustomization}
              onClick={saveOrgInfo}
            >
              {savingCustomization ? t('common.saving') : t('common.save')}
            </Button>
          </div>
          {testResult && (
            <MessageBar
              intent={testResult.ok ? 'success' : 'error'}
              style={{ marginTop: 12 }}
            >
              <MessageBarBody>{testResult.msg}</MessageBarBody>
            </MessageBar>
          )}
        </Card>
      )}

      {/* ===== Stats du tenant ===== */}
      {orgTab === 'm365' && hasActiveLicense && onboardingStatus?.connected && (
        <Card style={{ marginBottom: 24 }}>
          <CardHeader
            image={<PeopleTeam20Regular primaryFill={tokens.colorBrandForeground1}/>}
            header={<Title3>{t('tenants.stats.title')}</Title3>}
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginTop: 12 }}>
            <FieldBlock label={t('tenants.stats.m365Users')}>
              <Body1 style={{ fontWeight: 600 }}>
                {(typeof onboardingStatus.userCount === 'number'
                  ? onboardingStatus.userCount.toLocaleString(i18n.language)
                  : ((tenant.size as unknown as number)?.toString() ?? '—'))}
              </Body1>
              <Caption1 style={{ display: 'block', marginTop: 2, color: tokens.colorNeutralForeground3 }}>
                {t('tenants.stats.m365UsersSource')}
              </Caption1>
            </FieldBlock>
            <FieldBlock label={t('tenants.stats.entraIdSku')}>
              {onboardingStatus.entraIdSku ? (
                <Badge
                  appearance="filled"
                  color={onboardingStatus.entraIdSku === 'P2' ? 'brand'
                       : onboardingStatus.entraIdSku === 'P1' ? 'success'
                       : 'informative'}
                  style={{ fontWeight: 700 }}
                >
                  Entra ID {onboardingStatus.entraIdSku}
                </Badge>
              ) : (
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>—</Caption1>
              )}
              <Caption1 style={{ display: 'block', marginTop: 2, color: tokens.colorNeutralForeground3 }}>
                {t(`tenants.stats.entraSkuHint.${onboardingStatus.entraIdSku ?? 'unknown'}`, {
                  defaultValue: t('tenants.stats.entraSkuHint.unknown'),
                })}
              </Caption1>
            </FieldBlock>
            <FieldBlock label={t('tenants.stats.tenantId')}>
              <Body1 style={{ fontFamily: 'Consolas, monospace', fontSize: 12 }}>
                {onboardingStatus.tenantId?.slice(0, 8) + '…'}
              </Body1>
            </FieldBlock>
            <FieldBlock label={t('tenants.stats.verifiedDomains')}>
              <Body1>—</Body1>
              <Caption1 style={{ display: 'block', marginTop: 2, color: tokens.colorNeutralForeground3 }}>
                {t('tenants.stats.toEnrich')}
              </Caption1>
            </FieldBlock>
            <FieldBlock label={t('tenants.stats.lastScan')}>
              <Body1>{latestScan?.startedAt ? new Date(latestScan.startedAt).toLocaleDateString(i18n.language) : t('tenants.stats.noScan')}</Body1>
            </FieldBlock>
          </div>
          <Caption1 style={{ display: 'block', marginTop: 12, color: tokens.colorNeutralForeground3 }}>
            <Sparkle24Filled style={{ width: 12, height: 12, verticalAlign: 'middle', marginRight: 4, color: '#D9842B' }} />
            {t('tenants.stats.realtimeNote')}
          </Caption1>
        </Card>
      )}

      {/* ================================================================== */}
      {/* Encart Connexion Microsoft 365                                     */}
      {/* ================================================================== */}
      {orgTab === 'm365' && (
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div style={{
            width: 44, height: 44, borderRadius: 8,
            backgroundColor: onboardingStatus?.connected
              ? tokens.colorPaletteGreenBackground2
              : tokens.colorPaletteRedBackground2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <PlugConnected20Regular primaryFill={
              onboardingStatus?.connected
                ? tokens.colorPaletteGreenForeground1
                : tokens.colorPaletteRedForeground1
            } />
          </div>
          <div style={{ flex: 1, minWidth: 280 }}>
            <Title3 style={{ marginBottom: 4 }}>{t('tenants.connection.title')}</Title3>
            {onboardingStatus === null ? (
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                {t('tenants.connection.checking')}
              </Caption1>
            ) : onboardingStatus.connected ? (
              <>
                <Body1
                  style={{ color: tokens.colorNeutralForeground2 }}
                  dangerouslySetInnerHTML={{
                    __html: t('tenants.connection.connectedTo', { name: onboardingStatus.organizationName ?? '—' }),
                  }}
                />
                <Caption1 style={{ display: 'block', marginTop: 4, color: tokens.colorNeutralForeground3 }}>
                  {onboardingStatus.consentedAt
                    ? t('tenants.connection.consentOn', { date: new Date(onboardingStatus.consentedAt).toLocaleDateString(i18n.language) })
                    : t('tenants.connection.consentOn', { date: '—' })}
                  {(() => {
                    const cb = onboardingStatus.consentedBy;
                    if (!cb) return '';
                    const email = typeof cb === 'string' ? cb : cb.email;
                    return email ? t('tenants.connection.consentBy', { email }) : '';
                  })()}
                  {onboardingStatus.lastTestAt
                    ? t('tenants.connection.lastTest', {
                        date: new Date(onboardingStatus.lastTestAt).toLocaleString(i18n.language),
                        outcome: onboardingStatus.lastTestSuccess ? '✓' : '✗',
                      })
                    : ''}
                </Caption1>
              </>
            ) : (
              <Body1 style={{ color: tokens.colorPaletteRedForeground1 }}>
                {t('tenants.connection.noTenant')}
              </Body1>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
            {onboardingStatus?.connected && (
              <>
                <Button
                  appearance="secondary"
                  icon={<ArrowSync20Regular />}
                  onClick={runQuickTest}
                  disabled={testingConn}
                >
                  {testingConn ? t('tenants.connection.testing') : t('tenants.connection.test')}
                </Button>
                <Button
                  appearance="subtle"
                  icon={scriptLoading ? <Spinner size="tiny" /> : <ArrowDownload20Regular />}
                  onClick={onDownloadExoScript}
                  disabled={scriptLoading}
                >
                  {scriptLoading
                    ? t('tenants.connection.downloadingScript', 'Génération…')
                    : t('tenants.connection.downloadScript', 'Script accès EXO/Purview')}
                </Button>
                <Button
                  appearance="subtle"
                  onClick={onTestPermissions}
                  disabled={permsLoading}
                >
                  {permsLoading ? t('tenants.connection.testingPermissions') : t('tenants.connection.testPermissions')}
                </Button>
              </>
            )}
            <Button
              appearance="primary"
              onClick={() => navigate('/onboarding?force=1')}
            >
              {onboardingStatus?.connected ? t('tenants.connection.reconnect') : t('tenants.connection.launchWizard')}
            </Button>
          </div>
        </div>
        {testResult && (
          <MessageBar
            intent={testResult.ok ? 'success' : 'error'}
            style={{ marginTop: 12 }}
          >
            <MessageBarBody>{testResult.msg}</MessageBarBody>
          </MessageBar>
        )}

        {/* Sante des permissions Graph (calcule depuis le dernier scan) */}
        {permsHealth && (
          <div style={{
            marginTop: 12,
            padding: '10px 14px',
            background: permsHealth.ok ? tokens.colorPaletteGreenBackground1 : tokens.colorPaletteRedBackground1,
            border: `1px solid ${permsHealth.ok ? tokens.colorPaletteGreenBorderActive : tokens.colorPaletteRedBorderActive}`,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <span style={{
              fontSize: 18,
              color: permsHealth.ok ? tokens.colorPaletteGreenForeground1 : tokens.colorPaletteRedForeground1,
              fontWeight: 700,
            }}>
              {permsHealth.ok ? '✓' : '!'}
            </span>
            <div style={{ flex: 1 }}>
              <div style={{
                fontWeight: 600,
                fontSize: 13,
                color: permsHealth.ok ? tokens.colorPaletteGreenForeground1 : tokens.colorPaletteRedForeground1,
              }}>
                {permsHealth.ok
                  ? t('tenants.connection.permsOk')
                  : t('tenants.connection.permsKo', { n: permsHealth.totalErrors })}
              </div>
              <Caption1 style={{
                display: 'block',
                marginTop: 2,
                color: tokens.colorNeutralForeground2,
                fontSize: 11,
              }}>
                {permsHealth.ok
                  ? t('tenants.connection.permsOkBody', { n: permsHealth.totalChecks })
                  : t('tenants.connection.permsKoBody', { errs: permsHealth.totalErrors, total: permsHealth.totalChecks })}
              </Caption1>
            </div>
          </div>
        )}

        <Caption1 style={{ display: 'block', marginTop: 12, color: tokens.colorNeutralForeground3, fontSize: 11 }}>
          {t('tenants.connection.reconnectHint')}
        </Caption1>
      </Card>
      )}

      {/* Panel diagnostic permissions (collapsible) */}
      {orgTab === 'm365' && permsOpen && (
        <PermissionsDiagnosticPanel
          diag={permsDiag}
          loading={permsLoading}
          error={permsError}
          onClose={() => setPermsOpen(false)}
          onRerun={onTestPermissions}
        />
      )}

      {/* Périmètre du scan (déplacé depuis Mon compte) - onglet Microsoft 365 */}
      {orgTab === 'm365' && (
        <div style={{ marginBottom: 24 }}><ScanScopeSection /></div>
      )}

      {/* Carte connexion Azure (NIS Azure) - onglet Azure */}
      {orgTab === 'azure' && <AzureConnectionsSection />}

      {/* Carte identite tenant - onglet Organisation */}
      {orgTab === 'org' && (
      <Card
        style={{
          marginBottom: 24,
          borderLeft:   `4px solid ${tokens.colorBrandStroke1}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
          <div
            style={{
              width:           56,
              height:          56,
              borderRadius:    8,
              backgroundColor: tokens.colorBrandBackground2,
              display:         'flex',
              alignItems:      'center',
              justifyContent:  'center',
            }}
          >
            <Building24Regular primaryFill={tokens.colorBrandForeground1} />
          </div>
          <div>
            <Title3>{tenant.name}</Title3>
            <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
              {tenant.domain}
            </Caption1>
          </div>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        <div
          style={{
            display:             'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap:                 16,
          }}
        >
          <FieldBlock label={t('tenants.identityCard.tenantIdM365')}>
            <Body1 style={{ fontFamily: 'Consolas, monospace', fontSize: 12 }}>
              {tenant.m365TenantId}
            </Body1>
          </FieldBlock>

          <FieldBlock label={t('tenants.identityCard.industry')}>
            <Body1>{tenant.industry}</Body1>
          </FieldBlock>

          <FieldBlock label={t('tenants.identityCard.size')}>
            <Body1>
              <PeopleTeam20Regular
                style={{ verticalAlign: 'middle', marginRight: 4 }}
                primaryFill={tokens.colorNeutralForeground3}
              />
              {tenant.size != null && tenant.size > 0
                ? t('tenants.identityCard.sizeFmt', { n: tenant.size.toLocaleString(i18n.language) })
                : '-'}
            </Body1>
          </FieldBlock>

          <FieldBlock label={t('tenants.identityCard.activePlan')}>
            <Badge appearance="filled" color="brand">{currentTier.label}</Badge>
            <Caption1 style={{ display: 'block', marginTop: 4, color: tokens.colorNeutralForeground3 }}>
              {currentTier.monthlyEur === null
                ? t('tenants.identityCard.onQuote')
                : t('tenants.identityCard.priceFmt', { price: currentTier.monthlyEur })}
            </Caption1>
          </FieldBlock>

          <FieldBlock label={t('tenants.identityCard.connectedOn')}>
            <Body1>
              <Calendar20Regular
                style={{ verticalAlign: 'middle', marginRight: 4 }}
                primaryFill={tokens.colorNeutralForeground3}
              />
              {new Date(tenant.createdAt).toLocaleDateString(i18n.language)}
            </Body1>
          </FieldBlock>

          <FieldBlock label={t('tenants.identityCard.lastScan')}>
            {latestScan ? (
              <>
                <Body1>{new Date(latestScan.startedAt).toLocaleString(i18n.language)}</Body1>
                <Caption1 style={{ display: 'block', marginTop: 2, color: tokens.colorNeutralForeground3 }}>
                  {t('tenants.identityCard.scoreFmt', { score: tenant.lastScore ?? '?' })}
                </Caption1>
              </>
            ) : (
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{t('tenants.identityCard.noScan')}</Caption1>
            )}
          </FieldBlock>
        </div>
      </Card>
      )}

      {/* Plan mismatch warning */}
      {orgTab === 'org' && planMismatch && (
        <MessageBar intent="warning" style={{ marginBottom: 24 }}>
          <MessageBarBody>
            <span
              dangerouslySetInnerHTML={{
                __html: t('tenants.mismatch', {
                  n: tenant.size.toLocaleString(i18n.language),
                  recommended: recommended.label,
                  current: currentTier.label,
                }),
              }}
            />{' '}
            <RouterLink to="/billing" style={{ color: tokens.colorBrandForeground1 }}>{t('tenants.mismatchLink')}</RouterLink>.
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Avancés : À propos (déplacé depuis Mon compte) */}
      {orgTab === 'advanced' && (
        <Card>
          <CardHeader header={<Title3>{t('settings.about.title')}</Title3>} />
          <Body1 style={{ display: 'block', marginBottom: 6 }}>
            <strong>NIS365 Auditor</strong> {t('settings.about.version', { version: '0.2.0' })}
          </Body1>
          <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
            {t('settings.about.catalog', { ver: '2026.06.1', count: 70 })}
          </Caption1>
          <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block', marginTop: 8 }}>
            {t('settings.about.stack')}
          </Caption1>
        </Card>
      )}

    </div>
  );
}

// ----------------------------------------------------------------------------

function FieldBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block', marginBottom: 2 }}>
        {label}
      </Caption1>
      {children}
    </div>
  );
}

function scoreColor(score: number | undefined): string {
  if (score == null)   return statusColors.notApplicable;
  if (score >= 80)     return statusColors.pass;
  if (score >= 60)     return statusColors.warning;
  return statusColors.fail;
}

function scoreLabel(score: number | undefined, t: (k: string) => string): string {
  if (score == null)   return t('tenants.posture.noScan');
  if (score >= 90)     return t('tenants.posture.excellent');
  if (score >= 80)     return t('tenants.posture.good');
  if (score >= 60)     return t('tenants.posture.medium');
  if (score >= 40)     return t('tenants.posture.poor');
  return t('tenants.posture.critical');
}

// ============================================================================
//  Permissions Diagnostic Panel - affiche le resultat de
//  POST /api/onboarding/test-permissions, groupe par categorie, avec status et
//  hint par permission.
// ============================================================================
function PermissionsDiagnosticPanel({
  diag,
  loading,
  error,
  onClose,
  onRerun,
}: {
  diag:    TestPermissionsResponse | null;
  loading: boolean;
  error:   string | null;
  onClose: () => void;
  onRerun: () => void;
}) {
  const { t, i18n } = useTranslation();

  // Couleurs par status
  const statusColor = (s: PermissionTestResult['status']) => {
    switch (s) {
      case 'ok':         return { fg: statusColors.pass,    bg: statusColors.passBg };
      case 'license':    return { fg: statusColors.warning, bg: statusColors.warningBg };
      case 'notFound':   return { fg: statusColors.warning, bg: statusColors.warningBg };
      case 'forbidden':  return { fg: statusColors.fail,    bg: statusColors.failBg };
      case 'badReq':     return { fg: statusColors.fail,    bg: statusColors.failBg };
      case 'error':      return { fg: statusColors.error,   bg: statusColors.errorBg };
      default:           return { fg: tokens.colorNeutralForeground2, bg: tokens.colorNeutralBackground3 };
    }
  };

  // Header style brand
  return (
    <Card style={{ marginBottom: 16, borderLeft: `4px solid ${tokens.colorBrandStroke1}` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div>
          <Title3>{t('tenants.connection.permissionsTitle')}</Title3>
          {diag?.runAt && (
            <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block', marginTop: 2 }}>
              {t('tenants.connection.permissionsSubtitle', { date: new Date(diag.runAt).toLocaleString(i18n.language) })}
            </Caption1>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button appearance="subtle" onClick={onRerun} disabled={loading}>
            {t('tenants.connection.permissionsRerun')}
          </Button>
          <Button appearance="subtle" onClick={onClose}>
            {t('tenants.connection.permissionsClose')}
          </Button>
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Spinner label={t('tenants.connection.permissionsLoading', { count: 23 })} />
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <MessageBar intent="error">
          <MessageBarBody>{t('tenants.connection.permissionsError', { error })}</MessageBarBody>
        </MessageBar>
      )}

      {/* Resultats */}
      {!loading && diag && diag.summary && (
        <>
          {/* Summary banner */}
          <MessageBar
            intent={diag.summary.ko === 0 ? 'success' : 'warning'}
            style={{ marginBottom: 12 }}
          >
            <MessageBarBody>
              {diag.summary.ko === 0
                ? t('tenants.connection.permissionsOkSummary', { ok: diag.summary.ok, total: diag.summary.total })
                : t('tenants.connection.permissionsKoSummary', { ko: diag.summary.ko, total: diag.summary.total })}
            </MessageBarBody>
          </MessageBar>

          {/* Resultats groupes par categorie */}
          {Object.entries(groupByCategory(diag.results)).map(([cat, items]) => (
            <div key={cat} style={{ marginBottom: 16 }}>
              <Caption1 style={{
                display: 'block',
                marginBottom: 6,
                fontWeight: 700,
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: tokens.colorNeutralForeground2,
              }}>
                {t(`tenants.connection.permissionsCategory.${cat}`, { defaultValue: cat })}
              </Caption1>
              <div style={{
                background: tokens.colorNeutralBackground2,
                border: `1px solid ${tokens.colorNeutralStroke2}`,
                borderRadius: 8,
                overflow: 'hidden',
              }}>
                {items.map((p, i) => {
                  const c = statusColor(p.status);
                  return (
                    <div key={i} style={{
                      display: 'grid',
                      gridTemplateColumns: '24px 1fr auto',
                      gap: 12,
                      padding: '10px 14px',
                      borderTop: i === 0 ? 'none' : `1px solid ${tokens.colorNeutralStroke3}`,
                      alignItems: 'center',
                    }}>
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%',
                        background: c.fg, color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700,
                      }}>
                        {p.status === 'ok' ? '✓' : '!'}
                      </div>
                      <div>
                        <Body1 style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</Body1>
                        {(p.hint || p.error) && p.status !== 'ok' && (
                          <Caption1 style={{
                            display: 'block',
                            color: tokens.colorNeutralForeground2,
                            fontSize: 12,
                            marginTop: 2,
                          }}>
                            {p.hint ?? extractErrorMessage(p.error)}
                          </Caption1>
                        )}
                      </div>
                      <span style={{
                        padding: '3px 10px',
                        borderRadius: 999,
                        background: c.bg,
                        color: c.fg,
                        fontSize: 11,
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}>
                        {t(`tenants.connection.permissionsStatus.${p.status}`, { defaultValue: p.status })}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </>
      )}
    </Card>
  );
}

/**
 * Extrait un message d erreur lisible depuis la reponse Graph (qui retourne
 * souvent du JSON brut style {"error":{"code":"Forbidden","message":"..."}}).
 */
function extractErrorMessage(raw: string | undefined): string {
  if (!raw) return '';
  // Tente parse JSON
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.error?.message) return String(parsed.error.message);
    if (parsed?.message)         return String(parsed.message);
  } catch {
    // Pas du JSON - tente regex sur "message": "..."
    const m = raw.match(/"message"\s*:\s*"([^"]+)"/);
    if (m) return m[1];
  }
  // Fallback : tronque a 200 chars
  return raw.length > 200 ? raw.slice(0, 200) + '...' : raw;
}

function groupByCategory(results: PermissionTestResult[]): Record<string, PermissionTestResult[]> {
  const order = ['core', 'identity', 'security', 'purview', 'intune', 'exo', 'defender'];
  const out: Record<string, PermissionTestResult[]> = {};
  for (const r of results) {
    if (!out[r.category]) out[r.category] = [];
    out[r.category].push(r);
  }
  // Retourne dans l ordre defini ; les categories inconnues a la fin
  const ordered: Record<string, PermissionTestResult[]> = {};
  for (const cat of order) {
    if (out[cat]) ordered[cat] = out[cat];
  }
  for (const cat of Object.keys(out)) {
    if (!ordered[cat]) ordered[cat] = out[cat];
  }
  return ordered;
}

// =============================================================================
//  AzureConnectionsSection - liste des connections Azure du client + bouton
//  pour ouvrir le wizard d ajout. Cf. NIS Azure feature.
// =============================================================================
function AzureConnectionsSection() {
  const { t } = useTranslation();
  const [connections, setConnections] = useState<AzureConnection[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [wizardOpen, setWizardOpen]   = useState(false);

  useEffect(() => { void reload(); }, []);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const { connections: cs } = await azureConnectionsApi.list();
      setConnections(cs ?? []);
    } catch (e) {
      // 404 endpoint = backend pas encore deploye - on swallow gracieusement
      const err = e as Error & { status?: number };
      if (err.status !== 404) setError(err.message);
      setConnections([]);
    } finally {
      setLoading(false);
    }
  }

  async function removeConnection(id: string) {
    if (!confirm(t('tenants.azure.removeConfirm'))) return;
    try {
      await azureConnectionsApi.remove(id);
      await reload();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <>
      <Card
        style={{
          marginBottom: 24,
          padding:      24,
          display:      'flex',
          alignItems:   'center',
          gap:          20,
          flexWrap:     'wrap',
          background:   `linear-gradient(135deg, #E6F2F8 0%, #F5FAFD 100%)`,
          borderLeft:   `4px solid #0078D4`,
        }}
      >
        <div style={{
          width: 56, height: 56, borderRadius: 14,
          background: '#0078D4', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, fontWeight: 700, flexShrink: 0,
        }}>
          AZ
        </div>
        <div style={{ flex: 1, minWidth: 280 }}>
          <Title3 style={{ marginBottom: 4, color: '#1F3D5C' }}>{t('tenants.azure.title')}</Title3>
          {loading ? (
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{t('tenants.azure.loading')}</Caption1>
          ) : connections.length === 0 ? (
            <Body1 style={{ color: tokens.colorNeutralForeground2 }}>
              {t('tenants.azure.noneConnected')}
            </Body1>
          ) : (
            <Body1 style={{ color: tokens.colorNeutralForeground2 }}>
              {t('tenants.azure.countConnected', { count: connections.length })}
            </Body1>
          )}
          {error && (
            <Caption1 style={{ display: 'block', marginTop: 4, color: tokens.colorPaletteRedForeground1 }}>
              {error}
            </Caption1>
          )}
        </div>
        <Button
          appearance="primary"
          onClick={() => setWizardOpen(true)}
        >
          {t('tenants.azure.addButton')}
        </Button>
      </Card>

      {/* Liste des connections existantes */}
      {connections.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12, marginBottom: 24 }}>
          {connections.map((c) => (
            <Card key={c.id} style={{ padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, justifyContent: 'space-between' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Body1 style={{ fontWeight: 600 }}>{c.name}</Body1>
                  <Caption1 style={{ display: 'block', marginTop: 2, color: tokens.colorNeutralForeground3, fontFamily: 'Consolas, monospace', fontSize: 11 }}>
                    sub: {c.subscriptionId.slice(0, 8)}…
                  </Caption1>
                  <Caption1 style={{ display: 'block', marginTop: 2, color: tokens.colorNeutralForeground3, fontSize: 11 }}>
                    {c.status === 'ok' ? '✓ ' : c.status === 'error' ? '✗ ' : '◌ '}
                    {c.status === 'ok' ? t('tenants.azure.statusOk') : c.status === 'error' ? t('tenants.azure.statusError') : t('tenants.azure.statusUnknown')}
                  </Caption1>
                </div>
                <Button
                  size="small"
                  appearance="subtle"
                  onClick={() => removeConnection(c.id)}
                  style={{ color: tokens.colorPaletteRedForeground1 }}
                >
                  {t('tenants.azure.removeButton')}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <AzureConnectionWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={() => { setWizardOpen(false); void reload(); }}
      />
    </>
  );
}
